import { Descriptions, Tag, Card, List, Avatar, Button, Space, Image, Row, Col } from 'antd'
import { FileOutlined, PlayCircleOutlined, UserOutlined, EyeOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { ITeacherApplication } from '~/types/teacher-application'
import { getTeachingLanguages } from '~/utils/teaching-language'

interface ApplicationDetailsProps {
  application: ITeacherApplication
}

const ApplicationDetails = ({ application }: ApplicationDetailsProps) => {
  const teachingLanguages = getTeachingLanguages(application.languages, application.englishLevel)

  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      pending: { color: 'orange', text: '待审核' },
      approved: { color: 'green', text: '已通过' },
      rejected: { color: 'red', text: '已拒绝' },
    }
    const normalizedStatus = status.toLowerCase()
    const config = statusMap[normalizedStatus] || { color: 'default', text: status }
    return <Tag color={config.color}>{config.text}</Tag>
  }

  const handleFilePreview = (url?: string) => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
  }


  const isImageFile = (url: string) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp']
    return imageExtensions.some(ext => url.toLowerCase().includes(ext))
  }

  return (
    <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
      {/* 基本信息 */}
      <Card title="基本信息" size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="姓名">
            {application.firstName} {application.lastName}
          </Descriptions.Item>
          <Descriptions.Item label="邮箱">{application.email}</Descriptions.Item>
          <Descriptions.Item label="电话">{application.phone}</Descriptions.Item>
          <Descriptions.Item label="生日">{application.birthdate}</Descriptions.Item>
          <Descriptions.Item label="性别">{application.gender || '-'}</Descriptions.Item>
          <Descriptions.Item label="国籍">{application.nationality}</Descriptions.Item>
          <Descriptions.Item label="时区">{application.timezone}</Descriptions.Item>
          <Descriptions.Item label="状态">{getStatusTag(application.status)}</Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 教育背景 */}
      <Card title="教育背景" size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="学历">{application.education}</Descriptions.Item>
          <Descriptions.Item label="专业">{application.major || '-'}</Descriptions.Item>
          <Descriptions.Item label="学校">{application.university || '-'}</Descriptions.Item>
          <Descriptions.Item label="授课语言">
            {teachingLanguages.map(item => item.language).join(', ') || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="语言等级">
            {teachingLanguages.map(item => item.level).join(', ') || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="中文水平">{application.chineseLevel || '-'}</Descriptions.Item>
          <Descriptions.Item label="其他语言">{application.otherLanguages || '-'}</Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 教学经验 */}
      <Card title="教学经验" size="small" style={{ marginBottom: 16 }}>
        {application.teachingExperience.length > 0 ? (
          <List
            size="small"
            dataSource={application.teachingExperience}
            renderItem={(item) => (
              <List.Item>
                <List.Item.Meta
                  avatar={<Avatar icon={<UserOutlined />} />}
                  title={`${item.position} - ${item.institution}`}
                  description={
                    <div>
                      <div>工作时间: {item.duration}</div>
                      {item.description && <div>描述: {item.description}</div>}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <div style={{ textAlign: 'center', color: '#999' }}>暂无教学经验</div>
        )}
      </Card>

      {/* 工作详情 */}
      <Card title="工作详情" size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="时薪">{application.hourlyRate}</Descriptions.Item>
          <Descriptions.Item label="可用时间">
            {application.availability.length > 0 ? (
              <Space direction="vertical" size="small">
                {application.availability.map((time, index) => (
                  <Tag key={index}>{time}</Tag>
                ))}
              </Space>
            ) : (
              '-'
            )}
          </Descriptions.Item>
        </Descriptions>
        {application.additionalInfo && (
          <Descriptions>
            <Descriptions.Item label="附加信息" span={2}>
              {application.additionalInfo}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Card>

      {/* 紧急联系人 */}
      <Card title="紧急联系人" size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="联系人">{application.emergencyContact}</Descriptions.Item>
          <Descriptions.Item label="电话">{application.emergencyPhone}</Descriptions.Item>
          <Descriptions.Item label="关系" span={2}>
            {application.emergencyRelationship}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 文档资料 */}
      <Card title="文档资料" size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* 头像 */}
          {application.profilePhoto && (
            <div>
              <div style={{ marginBottom: 8 }}>
                <strong>头像:</strong>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Image
                  width={120}
                  height={120}
                  src={application.profilePhoto}
                  alt="头像"
                  style={{ 
                    objectFit: 'cover', 
                    borderRadius: '8px',
                    border: '1px solid #d9d9d9'
                  }}
                  preview={{
                    mask: <div><EyeOutlined /> 点击放大</div>
                  }}
                />
              </div>
            </div>
          )}
          
          {/* 证书 */}
          {application.certificates.length > 0 && (
            <div>
              <div style={{ marginBottom: 8 }}>
                <strong>证书:</strong>
              </div>
              <Row gutter={[16, 16]}>
                {application.certificates.map((cert, index) => (
                  <Col key={index} xs={12} sm={8} md={6}>
                    {isImageFile(cert) ? (
                      <div style={{ textAlign: 'center' }}>
                        <Image
                          width={100}
                          height={100}
                          src={cert}
                          alt={`证书${index + 1}`}
                          style={{ 
                            objectFit: 'cover', 
                            borderRadius: '4px',
                            border: '1px solid #d9d9d9'
                          }}
                          preview={{
                            mask: <div><EyeOutlined /> 点击放大</div>
                          }}
                        />
                        <div style={{ fontSize: '12px', marginTop: '4px', color: '#666' }}>
                          证书{index + 1}
                        </div>
                      </div>
                    ) : (
                      <Button
                        type="link"
                        icon={<FileOutlined />}
                        onClick={() => handleFilePreview(cert)}
                        style={{ padding: 0 }}
                      >
                        证书{index + 1}
                      </Button>
                    )}
                  </Col>
                ))}
              </Row>
            </div>
          )}
          
          {/* 学位证书 */}
          {application.degree && (
            <div>
              <div style={{ marginBottom: 8 }}>
                <strong>学位证书:</strong>
              </div>
              {isImageFile(application.degree) ? (
                <div style={{ textAlign: 'center' }}>
                  <Image
                    width={120}
                    height={120}
                    src={application.degree}
                    alt="学位证书"
                    style={{ 
                      objectFit: 'cover', 
                      borderRadius: '4px',
                      border: '1px solid #d9d9d9'
                    }}
                    preview={{
                      mask: <div><EyeOutlined /> 点击放大</div>
                    }}
                  />
                </div>
              ) : (
                <Button
                  type="link"
                  icon={<FileOutlined />}
                  onClick={() => handleFilePreview(application.degree)}
                  style={{ padding: 0 }}
                >
                  查看学位证书
                </Button>
              )}
            </div>
          )}
          
          {/* 身份证件 */}
          {application.idDocument && (
            <div>
              <div style={{ marginBottom: 8 }}>
                <strong>身份证件:</strong>
              </div>
              {isImageFile(application.idDocument) ? (
                <div style={{ textAlign: 'center' }}>
                  <Image
                    width={120}
                    height={120}
                    src={application.idDocument}
                    alt="身份证件"
                    style={{ 
                      objectFit: 'cover', 
                      borderRadius: '4px',
                      border: '1px solid #d9d9d9'
                    }}
                    preview={{
                      mask: <div><EyeOutlined /> 点击放大</div>
                    }}
                  />
                </div>
              ) : (
                <Button
                  type="link"
                  icon={<FileOutlined />}
                  onClick={() => handleFilePreview(application.idDocument)}
                  style={{ padding: 0 }}
                >
                  查看身份证件
                </Button>
              )}
            </div>
          )}
          
          {/* 简历 */}
          {application.resume && (
            <div>
              <strong>简历:</strong>
              <Button
                type="link"
                icon={<FileOutlined />}
                onClick={() => handleFilePreview(application.resume)}
                style={{ marginLeft: 8 }}
              >
                查看简历
              </Button>
            </div>
          )}
          
          {/* 个人介绍视频 */}
          {application.demoVideo && (
            <div>
              <strong>个人介绍视频:</strong>
              <Button
                type="link"
                icon={<PlayCircleOutlined />}
                onClick={() => handleFilePreview(application.demoVideo)}
                style={{ marginLeft: 8 }}
              >
                观看个人介绍视频
              </Button>
            </div>
          )}
        </Space>
      </Card>

      {/* 协议确认 */}
      <Card title="协议确认" size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={1} size="small">
          <Descriptions.Item label="同意条款">
            <Tag color={application.terms ? 'green' : 'red'}>
              {application.terms ? '已同意' : '未同意'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="背景调查">
            <Tag color={application.backgroundCheck ? 'green' : 'red'}>
              {application.backgroundCheck ? '已同意' : '未同意'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="营销推广">
            <Tag color={application.marketing ? 'green' : application.marketing === false ? 'red' : 'default'}>
              {application.marketing === true ? '已同意' : application.marketing === false ? '拒绝' : '未选择'}
            </Tag>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 审核信息 */}
      {(application.reviewNotes || application.reviewedBy || application.reviewedAt) && (
        <Card title="审核信息" size="small" style={{ marginBottom: 16 }}>
          <Descriptions column={1} size="small">
            {application.reviewNotes && (
              <Descriptions.Item label="审核备注">{application.reviewNotes}</Descriptions.Item>
            )}
            {application.reviewedBy && (
              <Descriptions.Item label="审核人">{application.reviewedBy}</Descriptions.Item>
            )}
            {application.reviewedAt && (
              <Descriptions.Item label="审核时间">
                {dayjs(application.reviewedAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      )}

      {/* 时间信息 */}
      <Card title="时间信息" size="small">
        <Descriptions column={2} size="small">
          <Descriptions.Item label="申请时间">
            {dayjs(application.createdAt).format('YYYY-MM-DD HH:mm:ss')}
          </Descriptions.Item>
          <Descriptions.Item label="更新时间">
            {dayjs(application.updatedAt).format('YYYY-MM-DD HH:mm:ss')}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  )
}

export default ApplicationDetails
